import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ACCOUNT_ACTIVITY_DEMO_PAGES,
  type ActivityEvent,
  type ActivityPage,
} from 'src/page-components/Accounts/AccountActivityTab/accountActivityDemoData'

/**
 * Single data seam for the card-detail Activity tab (EX-40600).
 *
 * Mirrors `useAccountStatementsData` for the sibling tab, and carries the same
 * rule: section components MUST read through this hook rather than importing
 * the demo pages directly, so wiring real data touches this file and nothing
 * else.
 *
 * EVERYTHING RETURNED HERE IS FABRICATED — deliberately, while the Activity
 * read endpoint is built. Keep the exposure in mind when changing the gates:
 * like Statements, this tab is opened by the SERVER capability
 * `READ_PHYSICAL_CARD_ACTIVITY` as well as by a client flag (see
 * `isUpdatedCardActivityEnabled`), so enrolling an organization on the backend
 * is by itself enough to show it an invented audit history. That is the
 * intended rollout, but the audience is set by backend enrollment rather than
 * by anything visible in this repo — widen it only on purpose.
 *
 * ## Why this is stateful rather than a constant
 *
 * Statements could return a frozen object because it fetches once. Activity is
 * CURSOR-PAGINATED (`?limit=&before=`, newest-first), so the accumulated feed,
 * the current cursor, and the in-flight state are real client state from the
 * start. Modelling that now — against fake pages — is the whole point: the
 * "load more" path, the terminal-page case, and the request-ordering hazards
 * below are exercised and tested today, so swapping in the endpoint cannot
 * quietly introduce them.
 *
 * ## Replacing this with the real endpoint
 *
 * The contract is frozen in extend-api `docs/card-management/activity-api-
 * contract.md` (EX-40583, PR #9999) and the demo types match it field-for-field,
 * so the change is confined to `fetchActivityPage` below: call
 * `GET /creditcards/{creditCardId}/cardmanagement/activity?limit=&before=` and
 * return its JSON. Everything else — accumulation, cursor handling, guards,
 * component props — stays as-is.
 *
 * Three things the swap must preserve, because the server enforces them:
 *
 * - `limit` must be 1..100; anything else is `400 / CARD-MGMT.2`.
 * - Cursors are OPAQUE and card-scoped. Never parse, construct, persist as
 *   domain data, or carry one across cards — the server rejects a cursor from
 *   another card or reset generation.
 * - While the non-production mock controller is what answers, responses carry
 *   `X-Extend-Card-Management-Mock: true`. That header is how you tell seeded
 *   data from an authoritative ledger read; a 200 alone is not evidence the
 *   real ledger exists.
 *
 * Errors reuse the Card Management envelope (`CARD-MGMT.2/.3/.4/.15`). This
 * hook exposes a single boolean `hasError` rather than a code, because the feed
 * renders one empty state for every failure; widen that only when the design
 * asks for per-code copy.
 */

/** The contract's default and maximum. `limit` outside 1..100 is a 400. */
const DEFAULT_PAGE_LIMIT = 50

/**
 * The seam a real endpoint replaces. Exported and injectable (see the hook's
 * second parameter) so tests can drive the guards below against a fetcher they
 * control — a foreign `creditCardId`, a rejected page, a cursor that never
 * terminates — rather than only through the demo Map's specific page shape.
 * Without that, the echo guard and the stale-request rejection are exercised
 * only by data that can never violate them.
 */
export type ActivityPageFetcher = (
  creditCardId: string,
  cursor: string | undefined,
  limit: number,
) => Promise<ActivityPage>

export interface AccountActivityData {
  /** The accumulated feed across every page loaded so far, newest first. */
  events: ActivityEvent[]
  /** True only for the FIRST page — "load more" reports through `isLoadingMore`. */
  isLoading: boolean
  /** True while a subsequent page is in flight. */
  isLoadingMore: boolean
  /** Any failure, first page or subsequent. */
  hasError: boolean
  /** False once the server returns a null `nextCursor` (terminal page). */
  hasMore: boolean
  /** No-op unless `hasMore` and nothing is already in flight. */
  loadMore: () => void
  /**
   * Re-requests the FIRST page from the start of the cursor walk.
   *
   * Needed because a failed first page is otherwise unrecoverable inside the
   * tab: `loadMore` early-returns without a cursor, so there is no path back to
   * page 1 short of navigating away and returning. That is survivable against
   * fabricated data and materially worse the moment the endpoint is a real
   * ledger read with a real failure rate.
   */
  retry: () => void
}

/**
 * The ONLY function the real endpoint replaces.
 *
 * Async and cursor-taking on purpose: it already has the signature and the
 * failure surface a `fetch` has, so the call sites around it do not change.
 */
export const fetchActivityPage: ActivityPageFetcher = async (
  creditCardId,
  cursor,
  /** Part of the frozen request shape; the server rejects anything outside 1..100. */
  limit,
) => {
  const page = ACCOUNT_ACTIVITY_DEMO_PAGES.get(cursor)

  // Mirrors the server's `400 / CARD-MGMT.2` for an unrecognized cursor. Worth
  // keeping: it means a cursor-handling bug fails loudly here rather than
  // silently returning an empty feed and only breaking against the real API.
  if (!page) {
    throw new Error('Unrecognized activity cursor')
  }

  // The demo pages are all smaller than the default limit, so this never
  // truncates today — it is here so the parameter is genuinely honoured rather
  // than decorative, and so a demo page grown past the limit behaves like the
  // server would instead of quietly over-returning.
  return {
    creditCardId,
    items: page.items.slice(0, limit),
    nextCursor: page.nextCursor,
  }
}

/**
 * @param creditCardId - The card whose activity to read. `undefined` while the
 *   card is still loading; no request is made until it resolves.
 */
export const useAccountActivityData = (
  creditCardId: string | undefined,
  fetchPage: ActivityPageFetcher = fetchActivityPage,
): AccountActivityData => {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  // Initialized TRUE, and that is the actual guard against a false empty state.
  // The fetch starts in an effect, so with `false` the very first render is
  // `!isLoading && !hasError && events.length === 0` — the feed's empty branch —
  // and React flushes passive effects after paint, so a user gets one painted
  // frame reading "No activity on this card yet." On an audit surface that is a
  // wrong answer, not a flicker.
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [hasReachedEnd, setHasReachedEnd] = useState(false)

  // Guards every `setState` below. Without it, a page that resolves after the
  // user has navigated away updates an unmounted tree, and — more importantly —
  // a page in flight when `creditCardId` changes would append ANOTHER card's
  // events onto this feed.
  const requestIdRef = useRef(0)
  // Read inside `loadMore` so the callback does not have to depend on the
  // in-flight booleans, which would give it a new identity on every request and
  // re-run any effect a caller keys on it.
  const isFetchingRef = useRef(false)
  // The fetcher is held in a ref rather than listed as a `loadPage` dependency
  // on purpose. A caller passing an inline function would otherwise change
  // `loadPage`'s identity every render, and `loadPage` is a dependency of the
  // first-page effect — that is a refetch loop, the exact hazard
  // `src/CLAUDE.md` calls out for effects that drive network work. Keeping it
  // here means no caller can destabilise the effect.
  const fetchPageRef = useRef(fetchPage)
  useEffect(() => {
    fetchPageRef.current = fetchPage
  }, [fetchPage])

  const loadPage = useCallback(
    async (cardId: string, before: string | undefined) => {
      if (isFetchingRef.current) {
        return
      }

      requestIdRef.current += 1
      const requestId = requestIdRef.current
      isFetchingRef.current = true

      const isFirstPage = before === undefined
      if (isFirstPage) {
        setIsLoading(true)
      } else {
        setIsLoadingMore(true)
      }
      setHasError(false)

      try {
        const page = await fetchPageRef.current(
          cardId,
          before,
          DEFAULT_PAGE_LIMIT,
        )

        if (requestId !== requestIdRef.current) {
          return
        }

        // The contract echoes `creditCardId` precisely so a client can reject a
        // response that belongs to another card. Dropping it is the safe branch:
        // rendering another card's audit history is a disclosure, not a glitch.
        if (page.creditCardId !== cardId) {
          setHasError(true)
          return
        }

        setEvents((previous) =>
          isFirstPage ? page.items : [...previous, ...page.items],
        )
        setCursor(page.nextCursor)
        setHasReachedEnd(page.nextCursor === null)
      } catch {
        if (requestId !== requestIdRef.current) {
          return
        }
        setHasError(true)
      } finally {
        if (requestId === requestIdRef.current) {
          isFetchingRef.current = false
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    },
    [],
  )

  // First page, and a full reset whenever the card changes — the accumulated
  // feed and the cursor both belong to the previous card and cannot carry over.
  useEffect(() => {
    if (!creditCardId) {
      return
    }

    requestIdRef.current += 1
    isFetchingRef.current = false
    setEvents([])
    setCursor(null)
    setHasReachedEnd(false)

    void loadPage(creditCardId, undefined)

    // Bumping the id on teardown is what actually makes the guard above cover
    // UNMOUNT, not just a card change: every `setState` in `loadPage` is behind
    // `requestId === requestIdRef.current`, so invalidating the id here means a
    // page still in flight when the tab unmounts resolves into no-ops instead of
    // writing to a torn-down tree. Clearing the in-flight flag keeps a remount
    // from inheriting a lock nothing will ever release.
    return () => {
      requestIdRef.current += 1
      isFetchingRef.current = false
    }
  }, [creditCardId, loadPage])

  const hasMore = !hasReachedEnd && cursor !== null

  const loadMore = useCallback(() => {
    if (!creditCardId || !cursor || hasReachedEnd || isFetchingRef.current) {
      return
    }
    void loadPage(creditCardId, cursor)
  }, [creditCardId, cursor, hasReachedEnd, loadPage])

  const retry = useCallback(() => {
    if (!creditCardId || isFetchingRef.current) {
      return
    }
    // Restarts the cursor walk from the beginning rather than resuming from the
    // stored cursor: the failure may have been the first page itself, and a
    // cursor from a partially-loaded walk is not a valid place to resume a feed
    // the user is about to see rebuilt.
    setEvents([])
    setCursor(null)
    setHasReachedEnd(false)
    void loadPage(creditCardId, undefined)
  }, [creditCardId, loadPage])

  return {
    events,
    // The initial `true` above is what covers the pre-effect render. This OR
    // additionally holds the loading state for an unresolved card: the prop is
    // typed `string | undefined`, though the only current caller reads it from
    // the matched route (`selectCreditCardIdFromRoute` returns `mtch.params.id`)
    // and so always has one. Kept because the effect early-returns without a
    // card, which would otherwise leave a card that resolved to `undefined`
    // sitting on a settled empty feed.
    isLoading: isLoading || creditCardId === undefined,
    isLoadingMore,
    hasError,
    hasMore,
    loadMore,
    retry,
  }
}
